/* eslint-disable no-restricted-imports */
import Link, { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import { Locale } from '../../../core/constants';
import isAbsoluteUrl from '../../../core/helpers/isAbsoluteUrl';

// This CustomLink component prevent us from passing locale as arg, and allows us to pass current locale with href,
// which prevents redirection from ja to the non-ja in _middleware safely.
// ref. https://tabist.atlassian.net/browse/DC-2224

// The reason why we don't use locale argument in the link is that the next link's hover-prefetch doesn't support passing locale arg, which causes the redirect.
// Note that it still prefetches the page with hovering even if prefetch is false in production build.
// https://nextjs.org/docs/pages/api-reference/components/link#prefetch

const CustomLink = ({
  children,
  ...props
}: React.PropsWithChildren<Omit<LinkProps, 'locale'>>): React.ReactElement => {
  const router = useRouter();
  const locale = router.locale === Locale.JAPANESE ? '' : `/${router.locale}`;

  const { href, ...rest } = props;

  if (typeof href === 'string') {
    // attach locale only when href is relative URL
    const link = isAbsoluteUrl(href) ? href : `${locale}${href}`;

    return (
      <Link
        legacyBehavior
        href={link}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
      >
        {children}
      </Link>
    );
  }

  const pathname =
    !href.pathname || isAbsoluteUrl(href.pathname) ? href.pathname : `${locale}${href.pathname}`;

  return (
    <Link
      legacyBehavior
      href={{
        pathname,
        ...href,
      }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    >
      {children}
    </Link>
  );
};

export default CustomLink;
